import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Deck, PokemonCard } from '../types/pokemon';

interface DeckCardMatch {
  deck_id: string;
  cards: {
    id: number;
    card_fr: string;
    energy: string;
  };
}

interface UsePublicDecksProps {
  sortBy: 'rating' | 'recent' | 'votes';
  userId?: string;
  page?: number;
  pageSize?: number;
  fetchAll?: boolean;
  searchTerm?: string;
  cardIds?: string[];
  cardFilterMode?: 'any' | 'all';  // Mode de filtrage des cartes: toutes ou au moins une
  includeVariants?: boolean;       // Inclure les variantes de cartes
  energy?: string | null;
}

export function usePublicDecks({ 
  sortBy, 
  userId, 
  page = 1, 
  pageSize = 10,
  fetchAll = false,
  searchTerm = '',
  cardIds = [],
  cardFilterMode = 'any',
  includeVariants = false,
  energy = null
}: UsePublicDecksProps) {
  const [decks, setDecks] = useState<(Deck & { 
    rating_score: number;
    total_votes: number;
  })[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDecks();
  }, [sortBy, userId, page, pageSize, fetchAll, searchTerm, cardIds, cardFilterMode, includeVariants, energy]);

  const fetchDecks = async () => {
    try {
      setLoading(true);
      
      // Pour le tri par note ou votes, nous devons récupérer tous les decks d'abord,
      // puis appliquer le tri côté client avant de paginer
      const needsClientSorting = sortBy === 'rating' || sortBy === 'votes';
      
      // Récupérer toutes les notes pour calculer les scores
      const { data: ratingsData } = await supabase
        .from('deck_ratings')
        .select('*');

      const ratingsMap = new Map(
        ratingsData?.map(rating => [rating.deck_id, rating]) || []
      );
      
      // Récupérer les decks qui contiennent les cartes spécifiées
      let filteredDeckIds: string[] | null = null;
      
      if (cardIds.length > 0 || energy) {
        // Construire la requête pour les cartes
        let cardsQuery = supabase
          .from('deck_cards')
          .select(`
            deck_id,
            cards:cards!inner (
              id,
              card_fr,
              energy
            )
          `);

        if (cardIds.length > 0) {
          cardsQuery = cardsQuery.in('card_id', cardIds);
        }

        if (energy) {
          cardsQuery = cardsQuery.eq('cards.energy', energy);
        }

        const { data: deckCardMatches } = await cardsQuery;

        if (deckCardMatches && deckCardMatches.length > 0) {
          // Logique différente selon le mode de filtrage
          if (cardFilterMode === 'all' && cardIds.length > 0) {
            // Pour le mode "all", nous devons trouver les decks qui contiennent TOUTES les cartes
            const deckCardGroups = (deckCardMatches as unknown as DeckCardMatch[]).reduce((groups, match) => {
              if (!groups[match.deck_id]) {
                groups[match.deck_id] = new Set<string>();
              }
              if (match.cards && cardIds.includes(match.cards.id.toString())) {
                groups[match.deck_id].add(match.cards.id.toString());
              }
              return groups;
            }, {} as Record<string, Set<string>>);

            filteredDeckIds = Object.entries(deckCardGroups)
              .filter(([_, cardSet]) => cardSet.size === cardIds.length)
              .map(([deckId]) => deckId);
          } else {
            // Pour le mode "any" ou le filtre d'énergie seul
            filteredDeckIds = [...new Set(deckCardMatches.map(match => match.deck_id))];
          }

          if (filteredDeckIds.length === 0) {
            setDecks([]);
            setTotalCount(0);
            setLoading(false);
            return;
          }
        } else {
          setDecks([]);
          setTotalCount(0);
          setLoading(false);
          return;
        }
      }
      
      // Construire la requête de base pour les decks
      let query = supabase
        .from('decks')
        .select(`
          *,
          profiles:user_id (
            username,
            avatar_url
          ),
          cards:deck_cards(
            quantity,
            card:cards(*)
          )
        `)
        .eq('is_public', true);

      // Construire la requête de comptage
      let countQuery = supabase
        .from('decks')
        .select('id', { count: 'exact', head: true })
        .eq('is_public', true);

      // Appliquer les filtres à la requête principale et à la requête de comptage
      if (userId) {
        query = query.eq('user_id', userId);
        countQuery = countQuery.eq('user_id', userId);
      }
      
      if (searchTerm && searchTerm.trim() !== '') {
        query = query.ilike('name', `%${searchTerm.trim()}%`);
        countQuery = countQuery.ilike('name', `%${searchTerm.trim()}%`);
      }
      
      if (filteredDeckIds && filteredDeckIds.length > 0) {
        query = query.in('id', filteredDeckIds);
        countQuery = countQuery.in('id', filteredDeckIds);
      }

      // Récupérer le nombre total de decks pour la pagination
      const { count: totalItems, error: countError } = await countQuery;
      
      if (countError) {
        console.error('Error counting decks:', countError);
        setTotalCount(0);
      } else {
        setTotalCount(totalItems || 0);
      }

      // Si nous pouvons trier directement dans la requête, on le fait
      if (sortBy === 'recent') {
        query = query.order('updated_at', { ascending: false });
      }
      
      let allDecks = [];
      
      if (needsClientSorting && (!fetchAll)) {
        // Si nous avons besoin de trier côté client pour rating ou votes, 
        // récupérer tous les decks, appliquer le tri, puis extraire la page demandée
        const { data, error: fetchError } = await query;
        
        if (fetchError) {
          console.error('Error fetching decks for client sorting:', fetchError);
          throw fetchError;
        }
        
        if (!data || data.length === 0) {
          setDecks([]);
          setLoading(false);
          return;
        }
        
        const processedDecks = data.map(deck => {
          const rating = ratingsMap.get(deck.id);
          return {
            ...deck,
            rating_score: rating?.rating_score || 0,
            total_votes: rating?.total_votes || 0
          };
        });
        
        // Trier selon le critère sélectionné
        if (sortBy === 'votes') {
          processedDecks.sort((a, b) => {
            // D'abord trier par nombre de votes
            if (b.total_votes !== a.total_votes) {
              return b.total_votes - a.total_votes;
            }
            // En cas d'égalité, trier par note
            return b.rating_score - a.rating_score;
          });
        } else if (sortBy === 'rating') {
          processedDecks.sort((a, b) => {
            // Tri par rating
            if (a.total_votes === 0 && b.total_votes === 0) {
              return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
            }
            if (a.total_votes === 0) return 1;
            if (b.total_votes === 0) return -1;
            
            // Si les notes sont différentes, trier par note
            if (b.rating_score !== a.rating_score) {
              return b.rating_score - a.rating_score;
            }
            
            // Si les notes sont égales, trier par nombre de votes
            return b.total_votes - a.total_votes;
          });
        }
        
        // Appliquer la pagination manuellement
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        allDecks = processedDecks.slice(start, end);
      } else {
        // Si nous n'avons pas besoin de tri spécial ou si fetchAll est vrai, 
        // on peut utiliser la pagination de Supabase
        if (!fetchAll) {
          const from = (page - 1) * pageSize;
          const to = from + pageSize - 1;
          query = query.range(from, to);
        }
        
        const { data, error: fetchError } = await query;
        
        if (fetchError) {
          console.error('Error fetching decks with pagination:', fetchError);
          throw fetchError;
        }
        
        if (!data) {
          setDecks([]);
          setLoading(false);
          return;
        }
        
        allDecks = data.map(deck => {
          const rating = ratingsMap.get(deck.id);
          return {
            ...deck,
            rating_score: rating?.rating_score || 0,
            total_votes: rating?.total_votes || 0
          };
        });
      }

      setDecks(allDecks);
    } catch (err) {
      console.error('Error fetching public decks:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      setDecks([]);
    } finally {
      setLoading(false);
    }
  };

  return { decks, totalCount, loading, error, currentPage: page, pageSize };
}